// SPDX-License-Identifier: Elastic-2.0
/**
 * Sidebar configuration (T-020, refreshed by F-330).
 *
 * The 14-entry, 3-section IA from functional spec § 2.2.
 *
 * Sections (in render order):
 *   HOGAR:    Home · Sugerencias · Automatizaciones · Dispositivos
 *   TU PLAN:  Mi cuenta · Mi plan · Acceso remoto (PLUS) · Backups · Voz (PLUS)
 *   PRO:      Hogares (PRO) · API Keys (PRO) · Marketplace · Publicar (PLUS) · Analytics (PRO)
 *
 * The old Avanzado / HA entry was removed and relocated to
 * AboutSection on /settings (see T-009).
 */
import { ROUTES } from "../../routing/routes";

export type SidebarItemId =
  | "home"
  | "suggestions"
  | "automations"
  | "devices"
  | "account"
  | "plan"
  | "remote_access"
  | "backups"
  | "voice"
  | "homes"
  | "api_keys"
  | "marketplace"
  | "publish"
  | "analytics";

export type SidebarIconName =
  | "home"
  | "sparkles"
  | "workflow"
  | "plug"
  | "user"
  | "crown"
  | "cloud"
  | "save"
  | "mic"
  | "homes"
  | "key"
  | "store"
  | "upload"
  | "chart";

/** Keys for reactive numeric badges. Narrow union — extend on demand. */
export type SidebarBadgeKey = "suggestionsPending";

export interface InternalSidebarItem {
  id: SidebarItemId;
  label_i18n: string;
  icon: SidebarIconName;
  to: string;
  /** Optional tier-gating chip (visual hint at MVP — no entitlement check). */
  tier?: "plus" | "pro";
  /** Optional reactive numeric badge. */
  badgeKey?: SidebarBadgeKey;
}

/** Public type retained for layout consumers — same shape as InternalSidebarItem post-refactor. */
export type SidebarItemConfig = InternalSidebarItem;

export interface SidebarSectionConfig {
  /** i18n key for the eyebrow heading. */
  title_i18n: string;
  items: readonly SidebarItemConfig[];
}

/**
 * The 14-entry / 3-section layout. Order is render order. Keep this stable —
 * tests in `sidebarConfig.test.ts` lock the contract (count, ids, tier badges,
 * badge keys) so accidental edits show up in CI.
 */
export const SIDEBAR_SECTIONS: readonly SidebarSectionConfig[] = [
  {
    title_i18n: "sidebar.section.home",
    items: [
      { id: "home", label_i18n: "sidebar.home", icon: "home", to: ROUTES.DASHBOARD },
      {
        id: "suggestions",
        label_i18n: "sidebar.suggestions",
        icon: "sparkles",
        to: ROUTES.SUGGESTIONS,
        badgeKey: "suggestionsPending",
      },
      {
        id: "automations",
        label_i18n: "sidebar.automations",
        icon: "workflow",
        to: ROUTES.ROUTINES,
      },
      { id: "devices", label_i18n: "sidebar.devices", icon: "plug", to: ROUTES.DEVICES },
    ],
  },
  {
    title_i18n: "sidebar.section.plan",
    items: [
      { id: "account", label_i18n: "sidebar.account", icon: "user", to: ROUTES.ACCOUNT },
      { id: "plan", label_i18n: "sidebar.plan", icon: "crown", to: ROUTES.PLAN },
      {
        id: "remote_access",
        label_i18n: "sidebar.remote_access",
        icon: "cloud",
        to: ROUTES.REMOTE_ACCESS,
        tier: "plus",
      },
      { id: "backups", label_i18n: "sidebar.backups", icon: "save", to: ROUTES.BACKUPS },
      {
        id: "voice",
        label_i18n: "sidebar.voice",
        icon: "mic",
        to: ROUTES.VOICE,
        tier: "plus",
      },
    ],
  },
  {
    title_i18n: "sidebar.section.pro",
    items: [
      {
        id: "homes",
        label_i18n: "sidebar.homes",
        icon: "homes",
        to: ROUTES.HOMES,
        tier: "pro",
      },
      {
        id: "api_keys",
        label_i18n: "sidebar.api_keys",
        icon: "key",
        to: ROUTES.API_KEYS,
        tier: "pro",
      },
      {
        id: "marketplace",
        label_i18n: "sidebar.marketplace",
        icon: "store",
        to: ROUTES.MARKETPLACE,
      },
      {
        id: "publish",
        label_i18n: "sidebar.publish",
        icon: "upload",
        to: ROUTES.PUBLISH,
        tier: "plus",
      },
      {
        id: "analytics",
        label_i18n: "sidebar.analytics",
        icon: "chart",
        to: ROUTES.ANALYTICS,
        tier: "pro",
      },
    ],
  },
] as const;

/** Flatten all items in render order. Handy for tests + active-route lookup. */
export function flattenItems(
  sections: readonly SidebarSectionConfig[] = SIDEBAR_SECTIONS,
): readonly SidebarItemConfig[] {
  return sections.flatMap((s) => s.items);
}
